import { ref, shallowRef, type Ref } from 'vue'
import { defineStore } from 'pinia'

export type UnsavedLeaveAction = 'save' | 'discard' | 'cancel'

export type UnsavedFloatingRegistration = {
  isDirty: Ref<boolean>
  saving: Ref<boolean>
  save: () => Promise<void>
  discard: () => void | Promise<void>
}

/**
 * Coordinates leave-confirmation dialogs and the global floating save bar for edit surfaces.
 */
export const useUnsavedChangesStore = defineStore('unsavedChanges', () => {
  const leaveDialogOpen = ref(false)
  let leaveResolver: ((action: UnsavedLeaveAction) => void) | null = null

  const floatingActive = shallowRef<UnsavedFloatingRegistration | null>(null)

  function requestLeaveConfirmation(): Promise<UnsavedLeaveAction> {
    leaveDialogOpen.value = true
    return new Promise((resolve) => {
      leaveResolver = resolve
    })
  }

  function settleLeave(action: UnsavedLeaveAction): void {
    leaveDialogOpen.value = false
    const r = leaveResolver
    leaveResolver = null
    r?.(action)
  }

  function registerFloating(reg: UnsavedFloatingRegistration): void {
    floatingActive.value = reg
  }

  function unregisterFloating(reg: UnsavedFloatingRegistration): void {
    if (floatingActive.value === reg) {
      floatingActive.value = null
    }
  }

  return {
    leaveDialogOpen,
    floatingActive,
    requestLeaveConfirmation,
    settleLeave,
    registerFloating,
    unregisterFloating,
  }
})
